import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, FileDown, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import * as GovernanceReportService from '@/services/governanceReportService';

interface GovernanceReportsProps {
  companyId: string;
}

export function GovernanceReports({ companyId }: GovernanceReportsProps) {
  const [reports, setReports] = useState<GovernanceReportService.GovernanceReportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  async function loadReports() {
    try {
      setLoading(true);
      const data = await GovernanceReportService.loadGovernanceReports(companyId);
      setReports(data);
    } catch (err: any) {
      console.error('Error loading reports:', err);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }

  async function generateReport() {
    try {
      setGenerating(true);
      const html = await GovernanceReportService.generateGovernanceReport(companyId);

      // Download the HTML report
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `governance-report-${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Governance report generated successfully');

      // Reload the reports list
      await loadReports();
    } catch (err: any) {
      console.error('Error generating report:', err);
      toast.error(err.message || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  }

  useEffect(() => {
    if (companyId) {
      loadReports();
    }
  }, [companyId]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Governance Reports</CardTitle>
        <div className="flex gap-2">
          <Button onClick={generateReport} disabled={generating}>
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Generating...
              </>
            ) : (
              <>
                <FileDown className="h-4 w-4 mr-2" />
                Generate Report
              </>
            )}
          </Button>
          <Button variant="outline" onClick={loadReports} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : reports.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Generated Date</TableHead>
                  <TableHead>User ID</TableHead>
                  <TableHead>File Path</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>{new Date(report.created_at).toLocaleString()}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {report.user_id.slice(0, 8)}...
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {report.details?.file_path || 'N/A'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => generateReport()}
                        title="Re-generate report"
                      >
                        <FileDown className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center p-8">
            <p className="text-sm text-muted-foreground">No governance reports generated yet.</p>
            <p className="text-xs text-muted-foreground mt-2">
              Click "Generate Report" to create your first compliance report.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
